import Slider from "react-slick"
import { useEffect, useRef, useState, useMemo } from "react"
import { SiteVideoData } from "../../../../interfaces"
import { SiteVideoInline } from "../../../../siteWidgets/videoPlayer/genericInlineVideoPlayer/siteVideoInline"
import { videoSourceOption } from "../../../topStory/windows/contentWindow/videoLiveSourceSelector"
require("./ynet_site_liveVideoPlayerComponent.less")

interface LiveVideoPlayerProps {
    videoTitle: string
    videoAutoPlay: boolean
    videoMuteSound: boolean
    liveVideoSource: videoSourceOption
    videoShowSoundBtn: boolean
    liveIndicationText?: string
    isMinimizedVisible: boolean
    videoShowControlPanel: boolean
    isMobileWeb?: boolean
    playerRefForExternal?: any
    externalIsOpenTrigger?: boolean
    isMinimizedDefaultView?: boolean
    onExternalIsOpenTriggerChange?: (isOpen: boolean) => void
    isFloating?: boolean,
    thisLocation: string
}

export const LiveVideoPlayer = (props: LiveVideoPlayerProps) => {

    const { thisLocation, isMinimizedDefaultView, videoTitle, liveVideoSource, videoAutoPlay, videoMuteSound, videoShowSoundBtn, videoShowControlPanel, isMobileWeb, isMinimizedVisible, externalIsOpenTrigger, playerRefForExternal, liveIndicationText, onExternalIsOpenTriggerChange } = props
    const [isPlayerOpen, setIsPlayerOpen] = useState(isMinimizedDefaultView ? false : true)
    const [isMuted, setIsMuted] = useState(videoMuteSound)
    const [isPlaying, setIsPlaying] = useState(videoAutoPlay)
    const [programTitleWidth, setProgramTitleWidth] = useState("0")
    const [isTitleOverFlowing, setIsTitleOverFlowing] = useState(null)
    const [isLiveIndicationHidden, setIsLiveIndicationHidden] = useState(false)
    const [isAlreadyOpenedOneTime, setIsAlreadyOpenedOneTime] = useState(false)
    const isPlayerOpenTrigger = externalIsOpenTrigger !== undefined ? externalIsOpenTrigger : isPlayerOpen;
    const playerRef: any = useRef<any>(null)
    const mobileBanner = typeof window !== "undefined" ? document.getElementById("ads.banner") : null;
    const mobileBottomHeight = mobileBanner ? mobileBanner.clientHeight : 75;
    const titleRef: any = useRef<HTMLElement>(null)
    const settings = {
        dots: false,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        speed: props.videoTitle.length * 200,
        autoplaySpeed: 0,
        rtl: true,
        arrows: false,
        cssEase: "linear"
    }

    const flowPlayerData: SiteVideoData = useMemo(() => {
        return {
            mediaType: "MEDIA_VIDEO",
            url: liveVideoSource ? liveVideoSource.lowResSrc : "",
            fullScreenUrl: liveVideoSource ? liveVideoSource.src : "",
            mediaId: "",
            poster: "",
            youtube_id: "",
            isLive: true,
            isMuteAvailable: true,
            blockAbroadViews: false,
            isSoundButtonVisible: videoShowSoundBtn,
            isPlayerBarVisible: videoShowControlPanel,
            isAutoPlaying: isMobileWeb ? true : videoAutoPlay,
            isFloating: true,
            thisLocation: thisLocation
        }
    }, [liveVideoSource, videoAutoPlay, videoShowControlPanel, videoShowSoundBtn, videoMuteSound])

    const getTitleOverflowStatus = () => {
        const titleElement = titleRef.current as HTMLElement
        const wrapperElement = document.querySelector(".live-video-player  .video-title-wrapper") as HTMLElement
        let titleOverflow = false
        if (titleElement) {
            titleOverflow = titleElement.offsetWidth > wrapperElement.offsetWidth
        }
        return titleOverflow
    }

    const updateProgramTitleWidth = () => {
        const titleElement = titleRef.current as HTMLElement

        if (titleElement) {
            setProgramTitleWidth(titleElement.offsetWidth + "px")
        }
    }

    useEffect(() => {
        setIsTitleOverFlowing(getTitleOverflowStatus())
        updateProgramTitleWidth()
    }, [videoTitle])

    useEffect(() => {

        return () => {
            if (playerRef.current) {
                playerRef.current.removeEventListener("play", onPLayEvent)
                playerRef.current.removeEventListener("pause", onPauseEvent)
                playerRef.current.removeEventListener("volumechange", handleVolumeChangeEvent)
            }
        }
    }, [])

    const onPLayEvent = () => {
        setIsPlaying(true)
    }

    const onPauseEvent = () => {
        setIsPlaying(false)
    }

    const handleVolumeChangeEvent = () => {
        if (playerRef.current.muted) {
            setIsMuted(true)
        } else {
            setIsMuted(false)
        }
    }

    const handleOpenPlayer = () => {
        if (externalIsOpenTrigger && onExternalIsOpenTriggerChange) {
            onExternalIsOpenTriggerChange(true)
        } else {
            setIsPlayerOpen(true)
        }

        if (isAlreadyOpenedOneTime) {
            if (playerRef.current) {
                playerRef.current.play();

                if (playerRef.current.muted) {
                    playerRef.current.toggleMute();
                }
            }
        } else {
            setIsAlreadyOpenedOneTime(true)
        }
    }

    const handleClosePlayer = () => {
        if (externalIsOpenTrigger && onExternalIsOpenTriggerChange) {
            onExternalIsOpenTriggerChange(false)
        } else {
            setIsPlayerOpen(false)
        }

        if (isPlaying) {
            playerRef.current.pause();
        }
    }

    const handleCloseLiveIndication = (e) => {
        e.stopPropagation();
        setIsLiveIndicationHidden(true);
    }

    const handlePlayerLoaded = (player) => {
        playerRef.current = player;
        if (playerRefForExternal) {
            playerRefForExternal.current = player;
        }
        player.setOpts({ muted: isMobileWeb ? true : videoMuteSound });

        if (playerRef.current) {
            playerRef.current.addEventListener("play", onPLayEvent)
            playerRef.current.addEventListener("pause", onPauseEvent)
            playerRef.current.addEventListener("volumechange", handleVolumeChangeEvent)
        }
    }

    const handlePlayerMuted = () => {
        if (playerRef.current) {
            playerRef.current.toggleMute();
        }
    }

    return (
        <div className={`live-video-player ${isMinimizedVisible ? "with-minimized" : "without-minimized"}`}>
            {isMinimizedVisible &&
                <div className={`minimized-live-indication ${isLiveIndicationHidden ? "hidden" : ""}`} onClick={handleOpenPlayer}>
                    <img className="live-gif" src={"https://www.ynet.co.il/images/video/live-and-dot-gif.gif"} alt="Live"></img>

                    <div className="close-btn" onClick={handleCloseLiveIndication}></div>
                </div>
            }

            <div className={`live-player-container ${isPlayerOpenTrigger ? "open" : "closed"}`} style={isMobileWeb && mobileBottomHeight ? { bottom: `${mobileBottomHeight}px` } : {}}>
                <SiteVideoInline
                    data={flowPlayerData}
                    isShareButton={false}
                    applyStickyness={false}
                    onPlayerLoaded={handlePlayerLoaded}
                />

                <div className="control-panel">
                    {/* <div className={`play-btn ${isPlaying ? "playing" : "paused"}`} onClick={handlePlayBtnClick}></div> */}

                    <div className={`mute-btn ${isMuted ? "muted" : "unmuted"}`} onClick={handlePlayerMuted}></div>

                    <div className="title-live-wrapper">
                        <div className="live-indication">
                            {/* <img className="dot-gif" src={"https://www.ynet.co.il/images/video/dot-gif.gif"} alt="Live"></img> */}

                            <div className="live-text">{liveIndicationText}</div>
                        </div>

                        <div className="video-title-wrapper">
                            {isTitleOverFlowing ?
                                <>
                                    <div className="fadder-right"></div>

                                    <div className="slider-wrapper" style={{ width: programTitleWidth }}>
                                        <Slider {...settings}>
                                            <div className="video-title" ref={ref => titleRef.current = ref}  dangerouslySetInnerHTML={{ __html: videoTitle }}></div>

                                            <div className="video-title" ref={ref => titleRef.current = ref}  dangerouslySetInnerHTML={{ __html: videoTitle }}></div>
                                        </Slider>
                                    </div>

                                    <div className="fadder-left"></div>
                                </>
                                :
                                <div className="video-title" ref={ref => titleRef.current = ref} dangerouslySetInnerHTML={{ __html: videoTitle }}></div>
                            }
                        </div>
                    </div>

                    <div className="close-btn" onClick={handleClosePlayer}></div>
                </div>
            </div>
        </div>
    )
}

LiveVideoPlayer.siteScriptName = "LiveVideoPlayer"